import React, { useContext, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { ModalContext } from '../../context';
import { Grid, TextField } from '@material-ui/core';
import { Autocomplete, Alert } from '@material-ui/lab';
import { FIXED_CHARGES, PERSPECTIVE_MAP } from '@/components/Accessorials/constants';
import Header from '@/components/Accessorials/Header';
import { PrimaryButton } from '@/styles/blocks';
import Charge from '@/components/Accessorials/Charge';
import { GET_PRICING_OVERRIDE_BY_ID } from '@/graphql/queries/pricing_overrides';
import { useLazyQuery, useQuery } from '@apollo/client';
import { QUERY_ADMIN_TAGS } from '@/components/admin/AdminTags/graphql';
import { H3, Body1 } from '@/components/Accessorials/blocks';
import { colors } from '@/styles';
import { isFinite } from 'lodash';
import { TabTitle } from '@/components/ShipmentForm/blocks';
import { ACCESSORIALS_BY_ALGO, genAccessorials } from '@onward-delivery/core';
import { UserContext } from '@/components/App';
import { useClientUser } from '@/hooks';
import { mergeTags } from '@/utilities/mergeTags';
import { useServiceCatalog } from '@/components/ShipmentForm/hooks/serviceCatalog';
import {
    buildServiceChargeOptions,
    chargeOptionGroup,
    mergeChargeOptions,
} from '@/components/Accessorials/serviceCharges';

/**
 * Rate breakdown for one pricing perspective: base charge and accessorials, editable with a comment
 * per edited line. Reads the order from `ModalContext` and saves through `callbacks.modifyOrder`, so
 * callers outside the Edit Order modal (RatesTab, the accessorial audit modal) wrap it in a
 * `ModalContext.Provider` shim.
 *
 * @param {object} props
 * @param {'internal'|'shipper'|'carrier'} [props.perspective='internal'] Which rate to show.
 * @param {boolean} [props.defaultExpanded=false] Start expanded; otherwise only `internal` does.
 * @param {boolean} [props.canEdit] Replaces the ownership check for edit access; the invoice lock still applies.
 */
const OrderDetailsPricing = ({ perspective, defaultExpanded = false, canEdit }) => {
    const { state, callbacks } = useContext(ModalContext);
    const [updates, setUpdates] = useState({});
    const [collapsed, setCollapsed] = useState(defaultExpanded ? false : perspective !== 'internal');
    const PERSPECTIVE = PERSPECTIVE_MAP[perspective || 'internal'];
    const { user, org_tags } = useContext(UserContext);
    const { client_id, teammateRoles } = useClientUser();
    const [newCharge, setNewCharge] = useState(null);
    const [baseChargeComment, setBaseChargeComment] = useState('');
    const [commentError, setCommentError] = useState(false);

    const [fetchOverride, { data: overrideData }] = useLazyQuery(GET_PRICING_OVERRIDE_BY_ID, {
        onError: (err) => {
            console.error(err);
        },
    });

    useEffect(() => {
        const pricing_override_id = state.order?.[PERSPECTIVE.pricing_override_id];
        if (pricing_override_id) {
            fetchOverride({ variables: { pricing_override_id } });
        }
    }, [state.order, PERSPECTIVE]);

    useEffect(() => {
        callbacks.onPendingChange?.(updates);
    }, [updates]);

    const updated = useMemo(() => {
        return {
            ...(state.order || {}),
            ...updates,
        };
    }, [state.order, updates]);

    const editable = useMemo(() => {
        const isAdmin = ['ADMIN', 'ONWARD_ADMIN'].some((role) => user?.roles?.[role]);
        if (isAdmin) return true;

        const isEditableWithInvoice = PERSPECTIVE.editable_with_invoice || !updated[PERSPECTIVE.invoice_id];

        // A parent may own the access decision (e.g. the accessorial audit modal, which grants
        // asset-heavy carrier admins edit rights on perspectives they aren't the client of).
        // The override replaces the ownership check only — the invoice lock still applies.
        if (typeof canEdit === 'boolean') return canEdit && isEditableWithInvoice;

        const isClient = user?.roles?.[PERSPECTIVE.role] && client_id === updated[PERSPECTIVE.id];
        const isInvoicingTeammate =
            user?.roles?.['TEAMMATE'] && client_id === updated[PERSPECTIVE.id] && teammateRoles?.invoicing;
        return (isClient || isInvoicingTeammate) && isEditableWithInvoice;
    }, [updated, user, client_id, PERSPECTIVE, teammateRoles, canEdit]);

    const qboTags = useMemo(() => {
        return updated?.[PERSPECTIVE.client]?.qbo_tags || [];
    }, [updated]);

    const { data: tagsData } = useQuery(QUERY_ADMIN_TAGS, { variables: {} });

    // EditOrderModal puts the catalog on context; the audit modal's shim does not. A null order
    // leaves the hook nothing to resolve, so it skips the fetch when context already has it.
    const { serviceCatalog: fetchedCatalog } = useServiceCatalog(state.serviceCatalog ? null : state.order);

    const catalogServices = useMemo(
        () => (state.serviceCatalog || fetchedCatalog)?.services || [],
        [state.serviceCatalog, fetchedCatalog]
    );

    const marketplaceTags = useMemo(() => {
        return [...(tagsData?.results || [])];
    }, [tagsData]);

    const rates = useMemo(() => {
        const rates = overrideData?.pricing_overrides_by_pk?.tariff?.rates || [];
        const grouped = (rates || []).reduce((acc, rate) => {
            const next = [...(acc[rate.type] || []), rate];
            acc[rate.type] = next.sort((l, r) => {
                if (l.min === r.min) {
                    return l.min2 - r.min2;
                }
                return l.min - r.min;
            });
            return acc;
        }, {});
        return grouped;
    }, [overrideData]);

    const [baseCharge, accessorials] = useMemo(() => {
        const algoType = updated?.price_breakdown?.[PERSPECTIVE.breakdown]?._meta?.type || 'DEFAULT';
        let base = 0;
        let accessorials = [];
        if (isFinite(updates[PERSPECTIVE.baseOverride])) {
            // In-progress local edit — highest priority
            base = updates[PERSPECTIVE.baseOverride];
            accessorials = updated?.order_revenue_adjustments?.[PERSPECTIVE.overrides] || [];
        } else if (isFinite(updated[PERSPECTIVE.baseOverride])) {
            // Previously saved override on the order (e.g. after "Save Charges" clears local state)
            base = updated[PERSPECTIVE.baseOverride];
            accessorials = updated?.order_revenue_adjustments?.[PERSPECTIVE.overrides] || [];
        } else {
            base = FIXED_CHARGES.reduce((acc, attr) => {
                return acc + (updated?.price_breakdown?.[PERSPECTIVE.breakdown]?.[attr] || 0);
            }, 0);
            accessorials =
                updated?.order_revenue_adjustments?.[PERSPECTIVE.overrides] ||
                updated?.price_breakdown?.[PERSPECTIVE.breakdown]?.accessorials ||
                [];
        }

        const breakdown = updated?.price_breakdown?.[PERSPECTIVE.breakdown] || {};
        return [
            {
                type: 'BASE_CHARGE',
                rate: base,
                quantity: 1,
                total: base,
                label: 'Base Charge',
                qbo_tag: breakdown.base_charge_qbo_tag || '',
                comment: baseChargeComment || breakdown.base_charge_comment || '',
                created_by: breakdown.base_charge_created_by || '',
                created_at: breakdown.base_charge_created_at || '',
                last_edited_by: breakdown.base_charge_last_edited_by || '',
                last_edited_at: breakdown.base_charge_last_edited_at || '',
            },
            accessorials.map((accessorial) => ({
                label: ACCESSORIALS_BY_ALGO[algoType]?.[accessorial.type]?.label || '',
                ...accessorial,
                total: accessorial.quantity * accessorial.rate,
            })),
        ];
    }, [updated, updates, PERSPECTIVE, baseChargeComment]);

    const options = useMemo(() => {
        const algoType = updated?.price_breakdown?.[PERSPECTIVE.breakdown]?._meta?.type || 'DEFAULT';
        const tags = mergeTags(updated?.[PERSPECTIVE.client]?.tags, org_tags);
        const accessorialOptions = genAccessorials(algoType, [...tags, ...marketplaceTags])
            .map((accessorial) => {
                const tagType = `${accessorial.type}`;
                const enrichments = rates[tagType];
                return {
                    ...accessorial,
                    qbo_tag: enrichments?.[0]?.qbo_tag || '',
                    rate: enrichments?.[0]?.rate || 0,
                    quantity: 1,
                    total: enrichments?.[0]?.rate || 0,
                    // Overwrite type with actual rate type name
                    type: tagType,
                };
            })
            .sort((a, b) => a.label.localeCompare(b.label));

        // Declared after `accessorials` — the dropdown hides services this leg already bills.
        return mergeChargeOptions(
            accessorialOptions,
            buildServiceChargeOptions(catalogServices, rates.SERVICE_RATE, accessorials)
        );
    }, [updated, PERSPECTIVE, org_tags, rates, marketplaceTags, catalogServices, accessorials]);

    const isBaseChargeEdited = useMemo(() => {
        return isFinite(updates[PERSPECTIVE.baseOverride]) || !!baseChargeComment;
    }, [updates, PERSPECTIVE, baseChargeComment]);

    const isAccessorialsEdited = useMemo(() => {
        return !!updated?.order_revenue_adjustments?.[PERSPECTIVE.overrides];
    }, [updated, PERSPECTIVE]);

    const [subtotal, accessorialsSum, total] = useMemo(() => {
        const subtotal = baseCharge?.total || 0;
        const accessorialsSum = accessorials.reduce((acc, accessorial) => {
            return acc + (accessorial.total || 0);
        }, 0);
        const total = subtotal + accessorialsSum;
        return [subtotal, accessorialsSum, total];
    }, [baseCharge, accessorials]);

    const hasEditedLinesWithoutComment = useMemo(() => {
        if (isBaseChargeEdited && !baseCharge?.comment) return true;
        if (isAccessorialsEdited) {
            return accessorials.some((a) => !a.is_auto_applied && !a.comment);
        }
        return false;
    }, [baseCharge, accessorials, isBaseChargeEdited, isAccessorialsEdited]);

    const onSave = () => {
        if (hasEditedLinesWithoutComment) {
            setCommentError(true);
            return;
        }
        setCommentError(false);
        callbacks.modifyOrder(updates);
        setUpdates({});
    };

    const onReset = () => {
        setCommentError(false);
        setUpdates({});
        setBaseChargeComment('');
        callbacks.modifyOrder({
            order_revenue_adjustments: {
                ...(state.order.order_revenue_adjustments || {}),
                [PERSPECTIVE.overrides]: null,
            },
            [PERSPECTIVE.baseOverride]: null,
        });
    };

    return (
        <>
            <TabTitle
                collapsed={collapsed}
                callbacks={{ setCollapsed }}
                css={css`
                    align-items: center;
                    justify-content: space-between;
                    ${collapsed ? 'margin-bottom: 20px;' : ''}
                `}
            >
                {PERSPECTIVE.title || 'Rate Breakdown'}
            </TabTitle>
            {collapsed ? null : (
                <Grid
                    container
                    direction="column"
                    css={css`
                        border: 1px solid #e0e0e0;
                        border-radius: 4px;
                        overflow: hidden;
                    `}
                >
                    {commentError && (
                        <Alert
                            severity="error"
                            css={css`
                                margin: 8px 16px 0;
                            `}
                        >
                            Please add a comment to all edited rates before saving.
                        </Alert>
                    )}

                    <Header backgroundColor="#f0f0f0" />

                    <Grid
                        container
                        direction="column"
                        css={css`
                            padding: 16px;
                        `}
                    >
                        <Charge
                            charge={baseCharge}
                            editable={editable}
                            qbo_tags={qboTags}
                            isBaseCharge
                            isEdited={isBaseChargeEdited}
                            callbacks={{
                                onChange: (value) => {
                                    setUpdates((prev) => {
                                        const clone = [...accessorials];

                                        return {
                                            ...prev,
                                            [PERSPECTIVE.baseOverride]: value,
                                            order_revenue_adjustments: {
                                                ...(updated?.order_revenue_adjustments || {}),
                                                [PERSPECTIVE.overrides]: clone,
                                            },
                                        };
                                    });
                                },
                                onCommentChange: (comment) => {
                                    setBaseChargeComment(comment);
                                },
                            }}
                        />
                        {accessorials.map((charge, idx) => {
                            return (
                                <Charge
                                    key={idx}
                                    charge={charge}
                                    options={options}
                                    rates={rates}
                                    editable={editable}
                                    qbo_tags={qboTags}
                                    isEdited={isAccessorialsEdited}
                                    callbacks={{
                                        onChange: (next) => {
                                            const now = new Date().toISOString();
                                            const userEmail = user?.email || '';
                                            const existing = accessorials[idx] || {};
                                            const auditedCharge = {
                                                ...next,
                                                created_by: existing.created_by || userEmail,
                                                created_at: existing.created_at || now,
                                                last_edited_by: userEmail,
                                                last_edited_at: now,
                                            };
                                            const clone = [...accessorials];
                                            clone.splice(idx, 1, auditedCharge);

                                            setUpdates((prev) => {
                                                return {
                                                    ...prev,
                                                    order_revenue_adjustments: {
                                                        ...(updated?.order_revenue_adjustments || {}),
                                                        [PERSPECTIVE.overrides]: clone,
                                                    },
                                                };
                                            });
                                        },
                                        onRemove: () => {
                                            setUpdates((prev) => {
                                                const clone = [...accessorials];
                                                clone.splice(idx, 1);

                                                return {
                                                    ...prev,
                                                    order_revenue_adjustments: {
                                                        ...(updated?.order_revenue_adjustments || {}),
                                                        [PERSPECTIVE.overrides]: clone,
                                                    },
                                                };
                                            });
                                        },
                                    }}
                                />
                            );
                        })}

                        <Grid
                            container
                            direction="row"
                            css={css`
                                margin-top: 24px;
                                margin-bottom: 12px;
                            `}
                        >
                            <PrimaryButton
                                css={css`
                                    margin-right: 24px;
                                `}
                                red
                                disabled={!editable}
                                onClick={() => {
                                    if (window.confirm(`Are you sure you want to reset these charges?`)) {
                                        onReset();
                                    }
                                }}
                            >
                                Reset Charges
                            </PrimaryButton>
                            <Autocomplete
                                size="small"
                                options={options}
                                groupBy={chargeOptionGroup}
                                getOptionLabel={(option) => option?.label || ''}
                                value={newCharge}
                                onChange={(_, value) => {
                                    setNewCharge(value);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        size="small"
                                        variant="outlined"
                                        placeholder="Select Charge..."
                                    />
                                )}
                                css={css`
                                    width: 200px;
                                    margin-right: 24px;
                                `}
                            />
                            <PrimaryButton
                                css={css`
                                    margin-right: 24px;
                                `}
                                disabled={!editable || !newCharge?.type}
                                onClick={() => {
                                    const now = new Date().toISOString();
                                    const userEmail = user?.email || '';
                                    setUpdates((prev) => {
                                        // SERVICE_RATE has one tariff row per service code, so
                                        // [0] would price every service as the first one.
                                        const defaultRate = isFinite(newCharge.rate)
                                            ? newCharge.rate
                                            : rates[newCharge.type]?.[0]?.rate || 0;
                                        const enriched = {
                                            ...newCharge,
                                            quantity: 1,
                                            rate: defaultRate,
                                            total: defaultRate,
                                            comment: '',
                                            created_by: userEmail,
                                            created_at: now,
                                            last_edited_by: userEmail,
                                            last_edited_at: now,
                                        };
                                        const clone = [...accessorials, enriched];
                                        return {
                                            ...prev,
                                            order_revenue_adjustments: {
                                                ...(updated?.order_revenue_adjustments || {}),
                                                [PERSPECTIVE.overrides]: clone,
                                            },
                                        };
                                    });
                                    setNewCharge(null);
                                }}
                            >
                                Add Charge
                            </PrimaryButton>
                            <PrimaryButton
                                css={css`
                                    margin-right: 24px;
                                `}
                                disabled={!editable}
                                onClick={() => {
                                    if (window.confirm(`Are you sure you want to save these charges?`)) {
                                        onSave();
                                    }
                                }}
                            >
                                Save Charges
                            </PrimaryButton>
                        </Grid>

                        <Grid
                            container
                            direction="row"
                            css={css`
                                margin-bottom: 8px;
                                padding: 0px 25px;
                            `}
                        >
                            <Grid
                                container
                                direction="column"
                                css={css`
                                    flex: 1;
                                    flex-basis: 0;
                                `}
                            >
                                <Body1>Base Rate</Body1>
                            </Grid>
                            <Grid
                                container
                                direction="column"
                                css={css`
                                    flex: 1;
                                    flex-basis: 0;
                                    align-items: flex-end;
                                `}
                            >
                                <H3>{isFinite(subtotal) ? subtotal.toFixed(2) : '--'}</H3>
                            </Grid>
                        </Grid>

                        <Grid
                            container
                            direction="row"
                            css={css`
                                margin-bottom: 20px;
                                padding: 0px 25px;
                                padding-bottom: 20px;
                                border-bottom: 1px solid #e0e0e0;
                            `}
                        >
                            <Grid
                                container
                                direction="column"
                                css={css`
                                    flex: 1;
                                    flex-basis: 0;
                                `}
                            >
                                <Body1>Accessorials</Body1>
                            </Grid>
                            <Grid
                                container
                                direction="column"
                                css={css`
                                    flex: 1;
                                    flex-basis: 0;
                                    align-items: flex-end;
                                `}
                            >
                                <H3
                                    css={css`
                                        color: ${colors.greens.primary};
                                    `}
                                >
                                    {isFinite(accessorialsSum) ? accessorialsSum.toFixed(2) : '--'}
                                </H3>
                            </Grid>
                        </Grid>
                        <Grid
                            container
                            direction="row"
                            css={css`
                                margin-bottom: 16px;
                            `}
                        >
                            <Grid
                                container
                                direction="column"
                                css={css`
                                    flex: 1;
                                    flex-basis: 0;
                                `}
                            />
                            <Grid
                                container
                                direction="column"
                                css={css`
                                    flex: 1;
                                    flex-basis: 0;
                                `}
                            >
                                <Grid
                                    container
                                    direction="row"
                                    css={css`
                                        padding: 0 25px;
                                    `}
                                >
                                    <Grid
                                        container
                                        direction="column"
                                        css={css`
                                            flex: 1;
                                            flex-basis: 0;
                                            justify-content: center;
                                        `}
                                    >
                                        <Body1>Total</Body1>
                                    </Grid>
                                    <Grid
                                        container
                                        direction="column"
                                        css={css`
                                            flex: 1;
                                            flex-basis: 0;
                                            justify-content: center;
                                            align-items: flex-end;
                                        `}
                                    >
                                        <H3>{isFinite(total) ? total.toFixed(2) : '--'}</H3>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            )}
        </>
    );
};

export default OrderDetailsPricing;
